'use client';
import { toZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DatesSetArg } from '@fullcalendar/core';
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock,
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Unlink,
  Video,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useUserStore } from '../../store/userStore';
import { toast } from 'react-toastify';
import { API_ROUTES } from '../../config/api-routes';
import axiosInstance from '../../api/api';
import 'react-toastify/dist/ReactToastify.css';
import LiveCalendar from './LiveCalendar';
import AppBackButton from '../common/AppBackButton';
import { useCalendarRealtimeRefresh } from '../../hooks/useCalendarRealtimeRefresh';
import { formatFreeSlotLabel, FREE_SLOT_COLOR } from '../../utils/calendarBusySlots';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import './PublicCalendarView.css';

const LiveCalendarCompat = LiveCalendar as React.ComponentType<any>;

export default function PublicCalendarView() {
  const user = useUserStore((state) => state.user);

  // Define type for BusySlot
  interface BusySlot {
    id: string;
    title: string | null;
    start: string;
    end: string;
    source: 'meeting' | 'manual' | 'integration' | string;
    providerEventId: string | null;
    blocksAvailability?: boolean;
    meetLink: string | null;
    location: string | null;
    user?: {
      firstName?: string;
      lastName?: string;
      email?: string;
    };
  }

  interface SavedCalendarEvent {
    id: number;
    start_time: string;
    end_time: string;
    booked?: boolean | string | number | null;
    notes?: string | null;
  }

  interface CalendarSettingsPayload {
    calendarStatus?: 'inactive' | 'active';
    integrationType?: 'google' | 'microsoft' | 'manual' | null;
    hasIntegrationTokens?: boolean;
  }
  // const [nextMeeting] = useState({
  //   exists: true,
  //   title: 'Interview with ABC Corp',
  //   time: 'Today, 2:00 PM',
  //   link: 'https://meet.google.com/example',
  // });

  const [pendingLoads, setPendingLoads] = useState(3); // 3 parallel fetches
  const isPageLoading = pendingLoads > 0;
  const markLoadDone = useCallback(() => setPendingLoads((n) => Math.max(0, n - 1)), []);

  const [showManualModal, setShowManualModal] = useState(false);
  const [editingFreeSlot, setEditingFreeSlot] = useState<{
    id: number;
    start: Date;
    end: Date;
  } | null>(null);
  const [freeSlotActionLoading, setFreeSlotActionLoading] = useState<number | null>(null);
  const [pendingDeleteSlot, setPendingDeleteSlot] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [showFreeSlotsPage, setShowFreeSlotsPage] = useState(false);
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState<{
    title: string;
    start: string;
    end: string;
    source?: string;
    notes?: string;
    link?: string | null;
    slotId?: number;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    id: string | null;
    type: 'accept' | 'deny' | null;
  }>({ id: null, type: null });

  // Tab state: 'upcoming' or 'history'
  const [meetingTab, setMeetingTab] = useState<'action' | 'upcoming' | 'history'>('action');
  const [isMeetingsCollapsed, setIsMeetingsCollapsed] = useState(false);

  const [calendarView, setCalendarView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'>(
    'dayGridMonth'
  );
  const calendarRef = useRef<FullCalendar | null>(null);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const toMonthValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || api.view.type === calendarView) {
      return;
    }
    api.changeView(calendarView);
  }, [calendarView]);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) {
      return;
    }

    const currentDate = api.getDate();
    const hasSameMonth =
      currentDate.getFullYear() === selectedMonth.getFullYear() &&
      currentDate.getMonth() === selectedMonth.getMonth();

    if (!hasSameMonth) {
      api.gotoDate(selectedMonth);
    }
  }, [selectedMonth]);

  // Track attendance for each meeting (meeting id -> attended boolean)
  const [attendanceStatus, setAttendanceStatus] = useState<Record<string, boolean | null>>({});

  // track calendar integration state so we can update the button and avoid
  // re‑asking the user to reconnect after a successful OAuth flow.
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  // Events fetched from Google/Microsoft
  // const integratedEvents = [
  //   {
  //     title: 'Google Meet: Project Sync',
  //     start: '2025-11-05T10:00:00',
  //     end: '2025-11-05T11:00:00',
  //     color: '#16a34a',
  //   },
  //   {
  //     title: 'Interview with ABC Corp',
  //     start: '2025-11-05T14:00:00',
  //     end: '2025-11-05T15:00:00',
  //     color: '#15803d',
  //   },
  // ];

  // ---------------------------------------------------------------------------
  // 🔹 FETCH CALENDAR SETTINGS (/livecalendar/me) - used to determine if a
  //    google/microsoft integration already exists so we can adjust the
  //    button text and state.
  // ---------------------------------------------------------------------------
  const loadCalendarSettings = useCallback(
    async (isInitial = false) => {
      setIsCheckingConnection(true);

      try {
        const res = await axiosInstance.get(API_ROUTES.LIVECALENDAR.ME_GET, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (res.data.success) {
          const settings: CalendarSettingsPayload = res.data.data || {};
          const hasProvider =
            settings.integrationType === 'google' || settings.integrationType === 'microsoft';
          const hasTokens = Boolean(settings.hasIntegrationTokens);
          const isActive = settings.calendarStatus === 'active';

          setIsGoogleConnected(hasProvider && hasTokens && isActive);
        } else {
          setIsGoogleConnected(false);
        }
      } catch (err) {
        console.error('Failed to load calendar settings', err);
        setIsGoogleConnected(false);
      } finally {
        setIsCheckingConnection(false);
        if (isInitial) markLoadDone();
      }
    },
    [markLoadDone]
  );

  useEffect(() => {
    loadCalendarSettings(true);
  }, [loadCalendarSettings]);

  useEffect(() => {
    const resetConnectingState = () => {
      const params = new URLSearchParams(window.location.search);
      const oauthSuccess =
        params.get('connected') === 'true' || params.get('oauth-success') === 'true';
      if (!oauthSuccess) {
        setIsConnecting(false);
      }
    };

    resetConnectingState();

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        resetConnectingState();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetConnectingState();
      }
    };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthCancelled = params.get('oauth_cancelled');
    const oauthError = params.get('oauth_error');

    if (oauthCancelled === '1') {
      setIsConnecting(false);
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth_cancelled');
      window.history.replaceState({}, '', url.toString());
      return;
    }

    if (!oauthError) {
      return;
    }

    setIsConnecting(false);
    const decoded = decodeURIComponent(oauthError.replace(/\+/g, ' '));
    const isBenignCancel = /cancel/i.test(decoded);
    if (!isBenignCancel) {
      toast.error(decoded);
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('oauth_error');
    window.history.replaceState({}, '', url.toString());
  }, []);

  // ---------------------------------------------------------------------------
  // 🔹 FETCH BUSY SLOTS (/livecalendar/me/slots)
  // ---------------------------------------------------------------------------
  const [busySlots, setBusySlots] = useState<BusySlot[]>([]);
  const [savedCalendarEvents, setSavedCalendarEvents] = useState<SavedCalendarEvent[]>([]);

  const loadSlots = useCallback(
    async (isInitial = false) => {
      try {
        const res = await axiosInstance.get(API_ROUTES.LIVECALENDAR.GET_BUSY_SLOTS, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (res.data.success) {
          setBusySlots(res.data.data);
          console.log('Busy Slots:', res.data.data);
        }
      } catch (err) {
        console.error('Failed to load busy slots', err);
      } finally {
        if (isInitial) markLoadDone();
      }
    },
    [markLoadDone]
  );

  useEffect(() => {
    loadSlots(true);
  }, [loadSlots]);

  const loadSavedCalendarEvents = useCallback(async () => {
    if (!user?.id) {
      setSavedCalendarEvents([]);
      return;
    }

    try {
      const res = await axiosInstance.get(`${API_ROUTES.CALENDAR.GET_USER_EVENTS}/${user.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      setSavedCalendarEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load saved calendar events', err);
    }
  }, [user?.id]);

  useEffect(() => {
    loadSavedCalendarEvents();
  }, [loadSavedCalendarEvents]);

  const [showCongratsModal, setShowCongratsModal] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('connected') || params.get('oauth-success');

    if (oauthSuccess === 'true') {
      // Show congratulations modal and mark as connected
      setShowCongratsModal(true);
      loadCalendarSettings();
      setIsConnecting(false);

      void (async () => {
        try {
          await axiosInstance.post(
            API_ROUTES.LIVECALENDAR.SYNC_GOOGLE_CALENDAR,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              },
            }
          );
          await loadSlots();
          toast.success('Google Calendar events synced as private busy times.');
        } catch (err) {
          console.error('Post-connect calendar sync failed', err);
        }
      })();

      // Remove the query param from the URL without reloading
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth-success');
      url.searchParams.delete('connected');
      window.history.replaceState({}, '', url.toString());
    }
  }, [loadCalendarSettings, loadSlots]);

  // ---------------------------------------------------------------------------
  // 🔹 FETCH MEETING REQUESTS (/livecalendar/requests/for-me)
  // ---------------------------------------------------------------------------
  const [meetingRequests, setMeetingRequests] = useState<any[]>([]);

  const loadRequests = useCallback(
    async (isInitial = false) => {
      try {
        const res = await axiosInstance.get(API_ROUTES.LIVECALENDAR.GET_MEETING_REQUESTS, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        console.log('Meeting Requests:', res.data);
        if (res.data.success) setMeetingRequests(res.data.data);
      } catch (err) {
        console.error('Failed fetching meeting requests:', err);
      } finally {
        if (isInitial) markLoadDone();
      }
    },
    [markLoadDone]
  );

  useEffect(() => {
    loadRequests(true);
  }, [loadRequests]);

  const refreshCalendarData = useCallback(() => {
    void loadRequests();
    void loadSlots();
    void loadSavedCalendarEvents();
  }, [loadRequests, loadSlots, loadSavedCalendarEvents]);

  useCalendarRealtimeRefresh(refreshCalendarData);

  const formatFreeSlotRange = useCallback((startIso: string, endIso: string) => {
    const start = new Date(startIso);
    const end = new Date(endIso);
    const dateLabel = start.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dateLabel}, ${startTime} – ${endTime}`;
  }, []);

  const requestDeleteFreeSlot = useCallback(
    (slot: Pick<SavedCalendarEvent, 'id' | 'start_time' | 'end_time'>) => {
      setPendingDeleteSlot({
        id: slot.id,
        label: formatFreeSlotRange(slot.start_time, slot.end_time),
      });
    },
    [formatFreeSlotRange]
  );

  const confirmDeleteFreeSlot = useCallback(async () => {
    if (!pendingDeleteSlot) return;

    const slotId = pendingDeleteSlot.id;
    setFreeSlotActionLoading(slotId);
    try {
      await axiosInstance.delete(`${API_ROUTES.CALENDAR.DELETE_EVENT}/${slotId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      toast.success('Free slot deleted.');
      setSelectedCalendarEvent(null);
      setPendingDeleteSlot(null);
      refreshCalendarData();
    } catch (error) {
      console.error('Failed to delete free slot', error);
      toast.error('Could not delete this free slot.');
    } finally {
      setFreeSlotActionLoading(null);
    }
  }, [pendingDeleteSlot, refreshCalendarData]);

  const openEditFreeSlot = useCallback((slot: SavedCalendarEvent) => {
    setEditingFreeSlot({
      id: slot.id,
      start: new Date(slot.start_time),
      end: new Date(slot.end_time),
    });
    setSelectedCalendarEvent(null);
    setShowFreeSlotsPage(false);
    setShowManualModal(true);
  }, []);

  // ---------------------------------------------------------------------------
  // 🔹 MAP BUSY SLOTS → FULLCALENDAR FORMAT
  // ---------------------------------------------------------------------------
  const mappedBusyEvents = useMemo(
    () =>
      busySlots.map((slot) => ({
        id: slot.id,
        title: slot.title || slot.source?.toUpperCase() || 'BUSY',
        start: slot.start,
        end: slot.end,
        color:
          slot.source === 'meeting' ? '#16a34a' : slot.source === 'manual' ? '#2563eb' : '#6b7280',
        extendedProps: {
          providerEventId: slot.providerEventId,
          source: slot.source,
          meetLink: slot.meetLink,
          location: slot.location,
          userName: slot.user?.firstName
            ? `${slot.user.firstName} ${slot.user.lastName}`
            : undefined,
        },
      })),
    [busySlots]
  );

  const mappedFreeSlotEvents = useMemo(
    () =>
      savedCalendarEvents
        .filter((event) => {
          const end = new Date(event.end_time);
          const isBooked = event.booked === true || event.booked === 'true' || event.booked === 1;
          return end.getTime() > Date.now() && !isBooked;
        })
        .map((slot) => ({
          id: `free-${slot.id}`,
          title: formatFreeSlotLabel(slot.notes),
          start: slot.start_time,
          end: slot.end_time,
          color: FREE_SLOT_COLOR,
          classNames: ['calendar-event--free-slot'],
          extendedProps: {
            source: 'free_slot',
            notes: slot.notes,
            slotId: slot.id,
          },
        })),
    [savedCalendarEvents]
  );

  const calendarEvents = useMemo(
    () => [
      ...mappedBusyEvents.filter((event) => event.extendedProps?.source !== 'manual'),
      ...mappedFreeSlotEvents,
    ],
    [mappedBusyEvents, mappedFreeSlotEvents]
  );

  async function approveMeeting(id: string) {
    setActionLoading({ id, type: 'accept' });
    try {
      const res = await axiosInstance.post(
        API_ROUTES.LIVECALENDAR.APPROVE_REQUEST(id),
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (res.data.success) {
        setMeetingRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: 'Approved' } : r))
        );
        await loadSlots();
        toast.success('Meeting approved successfully!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to approve meeting.');
    } finally {
      setActionLoading({ id: null, type: null });
    }
  }

  async function denyMeeting(id: string) {
    setActionLoading({ id, type: 'deny' });
    try {
      const res = await axiosInstance.post(
        API_ROUTES.LIVECALENDAR.DENY_REQUEST(id),
        { requestId: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (res.data.success) {
        setMeetingRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: 'Denied' } : r))
        );
        await loadSlots();
        setShowManualModal(true);
        toast.info(
          'Meeting rejected. Please add your free slots so the recruiter can pick a new time.'
        );
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to deny meeting.');
    } finally {
      setActionLoading({ id: null, type: null });
    }
  }

  const handleGoogleCalendarConnect = async () => {
    setIsConnecting(true);

    try {
      const currentPath = window.location.pathname;
      const payload = {
        redirectUrl: `${window.location.origin}${currentPath}`,
      };

      console.log('Initiating Google OAuth with payload:', payload);
      const res = await axiosInstance.post(
        '/livecalendar/integrations/oauth/initiate/google',
        payload
      );

      if (res.data?.success) {
        const url = res.data.data.redirectUrl;
        console.log('Redirecting to OAuth URL:', url);
        window.location.href = url; // Launch OAuth flow
      } else {
        console.error('OAuth failed', res.data);
        setIsConnecting(false);
        alert('Unable to start Google Calendar integration.');
      }
    } catch (error) {
      console.error('OAuth error', error);
      setIsConnecting(false);
      alert('Error connecting to Google Calendar.');
    }
  };

  const handleSyncGoogleCalendar = async () => {
    setIsConnecting(true);
    try {
      const res = await axiosInstance.post(
        API_ROUTES.LIVECALENDAR.SYNC_GOOGLE_CALENDAR,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      if (res.data?.success) {
        await loadSlots();
        toast.success(
          `Calendar synced (${res.data.imported ?? 0} new, ${res.data.updated ?? 0} updated). Event names stay private.`
        );
      }
    } catch (error) {
      console.error('Calendar sync error', error);
      toast.error('Failed to sync Google Calendar.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect from Google Calendar? Your busy slots will not be automatically updated.'
      )
    ) {
      return;
    }

    setIsConnecting(true);
    try {
      const res = await axiosInstance.post(
        API_ROUTES.LIVECALENDAR.DISCONNECT_GOOGLE,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (res.data?.success) {
        setIsGoogleConnected(false);
        await loadCalendarSettings();
        toast.success('Disconnected from Google Calendar');
      } else {
        toast.error('Failed to disconnect from Google Calendar');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Error disconnecting from Google Calendar');
    } finally {
      setIsConnecting(false);
    }
  };

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good Morning';
    } else if (hour < 16) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  // ---------------------------------------------------------------------------
  // 🔹 NEXT UPCOMING MEETING (Auto-computed)
  // ---------------------------------------------------------------------------
  // Show all meeting requests (don't filter by past dates)
  const futureMeetingRequests = useMemo(() => {
    return meetingRequests;
  }, [meetingRequests]);

  // Merge meeting requests with busy slots to get the correct link
  // (busy slots have meetLink or location URL, requests don't)
  const mappedRequests = futureMeetingRequests.map((req) => {
    const reqStart = new Date(`${req.meetingDate}T${req.startTime}:00`);
    const reqEnd = new Date(`${req.meetingDate}T${req.endTime}:00`);

    // Resolve meeting link by request id first, then by time overlap fallback.
    const correspondingSlot =
      busySlots.find((slot) => slot.providerEventId === req.id) ||
      busySlots.find((slot) => {
        const slotStart = new Date(slot.start);
        const slotEnd = new Date(slot.end);
        return (
          slot.source === 'meeting' &&
          slotStart.getTime() < reqEnd.getTime() &&
          slotEnd.getTime() > reqStart.getTime()
        );
      });

    const linkFromRequest =
      typeof req.location === 'string' && req.location.startsWith('http') ? req.location : null;

    const linkFromSlot = correspondingSlot
      ? correspondingSlot.meetLink ||
        (correspondingSlot.location?.startsWith('http') ? correspondingSlot.location : null)
      : null;

    const link = linkFromSlot || linkFromRequest;

    const meetingStart = new Date(`${req.meetingDate}T${req.startTime}:00`);
    const meetingEnd = new Date(`${req.meetingDate}T${req.endTime}:00`);

    return {
      id: req.id,
      title: req.reason === 'meeting' ? `Meeting with ${req.recruiterName}` : req.reason,
      date: `${req.meetingDate}, ${req.startTime} - ${req.endTime}`,
      status: req.status.charAt(0).toUpperCase() + req.status.slice(1).toLowerCase(),
      type: req.reason === 'meeting' ? 'Interview' : req.reason,
      attendance: req.attendance || null,
      meetingStart,
      meetingEnd,
      // Extract the actual meeting link from busy slot (check both meetLink and location URL)
      link: link && link.startsWith('http') ? link : null,
    };
  });

  // Separate meetings into action required, upcoming and history buckets
  const { actionMeetings, upcomingMeetings, pastMeetings } = useMemo(() => {
    const now = new Date();
    const action: typeof mappedRequests = [];
    const upcoming: typeof mappedRequests = [];
    const past: typeof mappedRequests = [];

    mappedRequests.forEach((m) => {
      const status = m.status.toLowerCase();
      const isFutureMeeting = m.meetingEnd.getTime() > now.getTime();

      if (status === 'pending') {
        action.push(m);
        return;
      }

      const shouldBeUpcoming =
        (status === 'approved' || status === 'rescheduled') && isFutureMeeting;

      if (shouldBeUpcoming) {
        upcoming.push(m);
      } else {
        past.push(m);
      }
    });

    return {
      actionMeetings: action,
      upcomingMeetings: upcoming,
      pastMeetings: past,
    };
  }, [mappedRequests]);

  // Filter out past slots from busy slots display
  const futureSlots = useMemo(() => {
    return busySlots;
  }, [busySlots]);

  // Inside your component
  const nextMeeting = useMemo(() => {
    const today = new Date();
    return futureSlots
      .filter((slot: any) => {
        const slotDate = toZonedTime(slot.start, slot.timezone || 'UTC');
        return (
          slotDate.getFullYear() === today.getFullYear() &&
          slotDate.getMonth() === today.getMonth() &&
          slotDate.getDate() === today.getDate() &&
          slotDate.getTime() > today.getTime()
        );
      })
      .sort(
        (a: any, b: any) =>
          toZonedTime(a.start, a.timezone || 'UTC').getTime() -
          toZonedTime(b.start, b.timezone || 'UTC').getTime()
      )[0];
  }, [futureSlots]);

  const candidateFreeSlots = useMemo(() => {
    return savedCalendarEvents
      .filter((event) => {
        const end = new Date(event.end_time);
        const isBooked = event.booked === true || event.booked === 'true' || event.booked === 1;
        return end.getTime() > Date.now() && !isBooked;
      })
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }, [savedCalendarEvents]);

  if (isPageLoading) {
    return <PageBusyLoader pageName="Calendar" />;
  }

  return (
    <div className="live-calendar-page min-h-screen bg-gray-50 p-4 md:p-6 flex flex-col gap-5">
      <div className="app-page-back-row">
        <AppBackButton to="/dashboard" />
      </div>
      <div className="live-calendar-hero">
        <h1 className="live-calendar-page-title">
          {getGreeting()}, {user?.firstName} 👋
        </h1>

        <div className="live-calendar-integration-row">
          <div
            className={`live-calendar-status-chip ${
              isCheckingConnection ? 'checking' : isGoogleConnected ? 'connected' : 'disconnected'
            }`}
          >
            <CheckCircle2 className="w-5 h-5" />
            <span>
              {isCheckingConnection
                ? 'Checking Google Calendar status...'
                : isGoogleConnected
                  ? 'Connected to Google Calendar'
                  : 'Google Calendar not connected'}
            </span>
          </div>

          {!isGoogleConnected ? (
            <button
              type="button"
              onClick={handleGoogleCalendarConnect}
              disabled={isConnecting || isCheckingConnection}
              className="live-calendar-connect-btn"
            >
              <Link2 className="live-calendar-action-icon" aria-hidden />
              {isConnecting ? 'Connecting...' : 'Connect to Google Calendar'}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => void handleSyncGoogleCalendar()}
                disabled={isConnecting}
                className="live-calendar-sync-btn"
              >
                <RefreshCw
                  className={`live-calendar-action-icon ${isConnecting ? 'live-calendar-action-icon--spin' : ''}`}
                  aria-hidden
                />
                {isConnecting ? 'Syncing...' : 'Sync calendar'}
              </button>
              <button
                type="button"
                onClick={handleGoogleCalendarDisconnect}
                disabled={isConnecting}
                className="live-calendar-disconnect-btn"
              >
                <Unlink className="live-calendar-action-icon" aria-hidden />
                {isConnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </>
          )}
        </div>

        {!nextMeeting && (
          <p className="live-calendar-hero-subtext">You have no meetings scheduled for today.</p>
        )}

        {nextMeeting && (
          <div className="mt-2 bg-white border border-green-100 rounded-xl shadow-sm px-6 py-4 flex items-center gap-4 max-w-2xl">
            <div className="p-3 bg-green-100 text-green-700 rounded-full">
              <Video className="w-5 h-5" />
            </div>
            <div className="flex flex-col flex-1">
              <p className="font-medium text-gray-900">
                {nextMeeting.title || nextMeeting.source?.toUpperCase() || 'BUSY'}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(nextMeeting.start).toLocaleString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 ml-4"
              onClick={() => {
                if (nextMeeting.meetLink) {
                  window.open(nextMeeting.meetLink, '_blank', 'noopener,noreferrer');
                }
              }}
              disabled={!nextMeeting.meetLink}
            >
              Join
            </Button>
          </div>
        )}
      </div>

      {/* 📅 Calendar Section */}
      <div className="flex flex-col gap-6 mt-2">
        {/* Left: Meetings List with Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6 h-fit lg:h-auto">
          <div className="live-calendar-section-header">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-green-700 shrink-0" />
              <h2 className="live-calendar-section-title">Upcoming Meetings</h2>
            </div>

            <div className="live-calendar-section-actions">
              <button
                type="button"
                className="live-calendar-icon-btn"
                title={isMeetingsCollapsed ? 'Expand meetings' : 'Collapse meetings'}
                onClick={() => setIsMeetingsCollapsed((prev) => !prev)}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isMeetingsCollapsed ? '-rotate-90' : 'rotate-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {!isMeetingsCollapsed && (
            <>
              <div className="live-calendar-tabs-row">
                <div className="flex gap-2 border-b border-gray-200 flex-1">
                  <button
                    onClick={() => setMeetingTab('action')}
                    className={`px-4 py-2 font-medium transition ${
                      meetingTab === 'action'
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Requires Action ({actionMeetings.length})
                  </button>
                  <button
                    onClick={() => setMeetingTab('upcoming')}
                    className={`px-4 py-2 font-medium transition ${
                      meetingTab === 'upcoming'
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Upcoming ({upcomingMeetings.length})
                  </button>
                  <button
                    onClick={() => setMeetingTab('history')}
                    className={`px-4 py-2 font-medium transition ${
                      meetingTab === 'history'
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    History ({pastMeetings.length})
                  </button>
                </div>
              </div>

              {/* Meetings List */}
              {meetingTab === 'action' ? (
                actionMeetings.length === 0 ? (
                  <p className="text-gray-400 text-sm">No meetings require action</p>
                ) : (
                  <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-2">
                    {actionMeetings.map((m) => (
                      <div
                        key={m.id}
                        className="border border-amber-200 bg-amber-50 rounded-lg p-4 transition"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-800">{m.title}</p>

                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                            {m.status}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <p>{m.date}</p>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">{m.type}</p>

                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              approveMeeting(m.id);
                            }}
                            disabled={actionLoading.id === m.id}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
                          >
                            {actionLoading.id === m.id && actionLoading.type === 'accept'
                              ? 'Accepting...'
                              : 'Accept'}
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              denyMeeting(m.id);
                            }}
                            disabled={actionLoading.id === m.id}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                          >
                            {actionLoading.id === m.id && actionLoading.type === 'deny'
                              ? 'Denying...'
                              : 'Deny'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : meetingTab === 'upcoming' ? (
                upcomingMeetings.length === 0 ? (
                  <p className="text-gray-400 text-sm">No upcoming meetings</p>
                ) : (
                  <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-2">
                    {upcomingMeetings.map((m) => (
                      <div
                        key={m.id}
                        className="border border-gray-100 rounded-lg p-4 hover:border-green-200 hover:shadow-sm transition"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-800">{m.title}</p>

                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              m.status === 'Approved'
                                ? 'bg-green-100 text-green-700'
                                : m.status === 'Denied'
                                  ? 'bg-red-100 text-red-700'
                                  : m.status === 'Pending'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : m.status === 'Rescheduled'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-700' // fallback
                            }`}
                          >
                            {m.status}
                          </span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <p>{m.date}</p>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">{m.type}</p>

                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (m.link) {
                                window.open(m.link, '_blank', 'noopener,noreferrer');
                              } else {
                                toast.info('No meeting link available for this card yet.');
                              }
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            Join <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* ACTION BUTTONS */}
                        {m.status === 'Pending' && m.status !== 'Denialed' && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                approveMeeting(m.id);
                              }}
                              disabled={actionLoading.id === m.id}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                              {actionLoading.id === m.id && actionLoading.type === 'accept'
                                ? 'Accepting...'
                                : 'Accept'}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                denyMeeting(m.id);
                              }}
                              disabled={actionLoading.id === m.id}
                              className="px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                              {actionLoading.id === m.id && actionLoading.type === 'deny'
                                ? 'Denying...'
                                : 'Deny'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : pastMeetings.length === 0 ? (
                <p className="text-gray-400 text-sm">No past meetings</p>
              ) : (
                <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto pr-2">
                  {pastMeetings.map((m) => (
                    <div
                      key={m.id}
                      className="border border-gray-100 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition"
                    >
                      {(() => {
                        const resolvedAttendance =
                          attendanceStatus[m.id] ??
                          (m.attendance === 'attended'
                            ? true
                            : m.attendance === 'no-show'
                              ? false
                              : null);

                        return (
                          <>
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-800">{m.title}</p>

                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  m.status === 'Approved'
                                    ? 'bg-green-100 text-green-700'
                                    : m.status === 'Denied'
                                      ? 'bg-red-100 text-red-700'
                                      : m.status === 'Pending'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : m.status === 'Rescheduled'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {m.status}
                              </span>
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="w-4 h-4" />
                              <p>{m.date}</p>
                            </div>
                            <p className="mt-1 text-xs text-gray-400">{m.type}</p>

                            <div className="mt-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (m.link) {
                                    window.open(m.link, '_blank', 'noopener,noreferrer');
                                  } else {
                                    toast.info('No meeting link available for this card yet.');
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                              >
                                Join <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {/* Attendance Tracking */}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs font-medium text-gray-700 mb-2">Attendance:</p>
                              <div className="flex gap-2">
                                <button
                                  onClick={async () => {
                                    if (resolvedAttendance !== null) {
                                      return;
                                    }

                                    try {
                                      await axiosInstance.patch(
                                        API_ROUTES.LIVECALENDAR.ATTENDANCE_UPDATE(m.id),
                                        { attendance: 'attended' },
                                        {
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem(
                                              'accessToken'
                                            )}`,
                                          },
                                        }
                                      );
                                      setAttendanceStatus((prev) => ({
                                        ...prev,
                                        [m.id]: true,
                                      }));
                                      setMeetingRequests((prev: any[]) =>
                                        prev.map((r) =>
                                          r.id === m.id ? { ...r, attendance: 'attended' } : r
                                        )
                                      );
                                      toast.success('Attendance marked as attended');
                                    } catch (err) {
                                      console.error(err);
                                      toast.error('Failed to update attendance');
                                    }
                                  }}
                                  disabled={resolvedAttendance !== null}
                                  className={`px-3 py-1 text-xs rounded-md transition ${
                                    resolvedAttendance === true
                                      ? 'bg-green-600 text-white'
                                      : resolvedAttendance !== null
                                        ? 'border border-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  ✓ Attended
                                </button>
                                <button
                                  onClick={async () => {
                                    if (resolvedAttendance !== null) {
                                      return;
                                    }

                                    try {
                                      await axiosInstance.patch(
                                        API_ROUTES.LIVECALENDAR.ATTENDANCE_UPDATE(m.id),
                                        { attendance: 'no-show' },
                                        {
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem(
                                              'accessToken'
                                            )}`,
                                          },
                                        }
                                      );
                                      setAttendanceStatus((prev) => ({
                                        ...prev,
                                        [m.id]: false,
                                      }));
                                      setMeetingRequests((prev: any[]) =>
                                        prev.map((r) =>
                                          r.id === m.id ? { ...r, attendance: 'no-show' } : r
                                        )
                                      );
                                      toast.success('Attendance marked as no-show');
                                    } catch (err) {
                                      console.error(err);
                                      toast.error('Failed to update attendance');
                                    }
                                  }}
                                  disabled={resolvedAttendance !== null}
                                  className={`px-3 py-1 text-xs rounded-md transition ${
                                    resolvedAttendance === false
                                      ? 'bg-red-600 text-white'
                                      : resolvedAttendance !== null
                                        ? 'border border-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  ✗ No-show
                                </button>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Monthly Calendar (FullCalendar) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-green-700 shrink-0" />
              <h2 className="live-calendar-section-title">Monthly Calendar</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                className="border-green-200 text-green-700 hover:bg-green-50 text-sm"
                onClick={() => setShowFreeSlotsPage(true)}
              >
                Your Free Slots ({candidateFreeSlots.length})
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-sm"
                onClick={() => {
                  setEditingFreeSlot(null);
                  setShowManualModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1.5" /> Add Free Slots
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <p className="text-sm text-gray-500 mb-0">Your schedule and availability</p>
            <div className="live-calendar-view-controls live-calendar-calendar-controls">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-500 shrink-0" />
                <select
                  title="Month selector"
                  value={toMonthValue(selectedMonth)}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split('-');
                    setSelectedMonth(new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1));
                  }}
                  className="bg-white border border-gray-300 rounded px-3 py-1.5 text-sm font-medium text-gray-700"
                >
                  {(() => {
                    const months = [];
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    for (let i = 0; i < 12; i++) {
                      const date = new Date(currentYear, i, 1);
                      const dateStr = toMonthValue(date);
                      const label = date.toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      });
                      months.push(
                        <option key={dateStr} value={dateStr}>
                          {label}
                        </option>
                      );
                    }
                    return months;
                  })()}
                </select>
              </div>
              <div className="flex gap-1 bg-gray-100 rounded p-1">
                <button
                  type="button"
                  onClick={() => setCalendarView('dayGridMonth')}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    calendarView === 'dayGridMonth'
                      ? 'bg-green-600 text-white'
                      : 'text-gray-700 hover:bg-white'
                  }`}
                >
                  Month
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarView('timeGridWeek')}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    calendarView === 'timeGridWeek'
                      ? 'bg-green-600 text-white'
                      : 'text-gray-700 hover:bg-white'
                  }`}
                >
                  Week
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarView('timeGridDay')}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    calendarView === 'timeGridDay'
                      ? 'bg-green-600 text-white'
                      : 'text-gray-700 hover:bg-white'
                  }`}
                >
                  Day
                </button>
              </div>
            </div>
          </div>

          <FullCalendar
            ref={calendarRef}
            timeZone="local"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={selectedMonth}
            headerToolbar={{
              left: 'prev,next',
              center: 'title',
              right: '',
            }}
            datesSet={(arg: DatesSetArg) => {
              const viewType = arg.view.type;
              if (
                viewType === 'dayGridMonth' ||
                viewType === 'timeGridWeek' ||
                viewType === 'timeGridDay'
              ) {
                setCalendarView((prev) => (prev === viewType ? prev : viewType));
              }

              const referenceDate =
                viewType === 'dayGridMonth'
                  ? arg.view.currentStart
                  : calendarRef.current?.getApi().getDate() || arg.start;

              const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);

              setSelectedMonth((prev) => {
                const sameMonth =
                  prev.getFullYear() === monthStart.getFullYear() &&
                  prev.getMonth() === monthStart.getMonth();
                return sameMonth ? prev : monthStart;
              });
            }}
            dayCellClassNames={(arg) => {
              const today = new Date();
              if (
                arg.date.getFullYear() === today.getFullYear() &&
                arg.date.getMonth() === today.getMonth() &&
                arg.date.getDate() === today.getDate()
              ) {
                return ['today-highlight'];
              }
              return [];
            }}
            dayCellDidMount={(arg) => {
              const today = new Date();
              if (
                arg.date.getFullYear() === today.getFullYear() &&
                arg.date.getMonth() === today.getMonth() &&
                arg.date.getDate() === today.getDate()
              ) {
                const badge = document.createElement('span');
                badge.textContent = 'Today';
                badge.className =
                  'absolute bottom-1 right-1 text-xs bg-green-600 text-white px-2 py-1 rounded-full font-semibold';
                arg.el.style.position = 'relative';
                arg.el.appendChild(badge);
              }
            }}
            events={calendarEvents}
            dayMaxEventRows={3}
            eventDisplay="block"
            height="620px"
            nowIndicator={calendarView !== 'dayGridMonth'}
            allDaySlot={calendarView === 'dayGridMonth'}
            slotMinTime="07:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:30:00"
            slotLabelInterval="01:00:00"
            scrollTime="08:00:00"
            stickyHeaderDates
            expandRows
            dayHeaderFormat={
              calendarView === 'dayGridMonth'
                ? { weekday: 'short' }
                : { weekday: 'short', month: 'short', day: 'numeric' }
            }
            eventContent={(arg) => {
              const isFreeSlot = arg.event.extendedProps?.source === 'free_slot';
              const slotId = arg.event.extendedProps?.slotId as number | undefined;
              const eventStart = arg.event.start;
              const eventEnd = arg.event.end;

              return (
                <div className="calendar-event-chip">
                  <span className="calendar-event-chip-time">{arg.timeText}</span>
                  <span className="calendar-event-chip-title">{arg.event.title}</span>
                  {isFreeSlot && slotId ? (
                    <span className="calendar-event-chip-actions">
                      <button
                        type="button"
                        className="calendar-event-chip-action"
                        aria-label="Edit free slot"
                        title="Edit"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!eventStart || !eventEnd) return;
                          openEditFreeSlot({
                            id: slotId,
                            start_time: eventStart.toISOString(),
                            end_time: eventEnd.toISOString(),
                          });
                        }}
                      >
                        <Pencil size={11} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="calendar-event-chip-action calendar-event-chip-action--danger"
                        aria-label="Delete free slot"
                        title="Delete"
                        disabled={freeSlotActionLoading === slotId}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!eventStart || !eventEnd) return;
                          void requestDeleteFreeSlot({
                            id: slotId,
                            start_time: eventStart.toISOString(),
                            end_time: eventEnd.toISOString(),
                          });
                        }}
                      >
                        <Trash2 size={11} aria-hidden />
                      </button>
                    </span>
                  ) : null}
                </div>
              );
            }}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            }}
            eventClick={(info) => {
              const eventLink =
                info.event.extendedProps?.meetLink ||
                (typeof info.event.extendedProps?.location === 'string' &&
                info.event.extendedProps.location.startsWith('http')
                  ? info.event.extendedProps.location
                  : null);

              setSelectedCalendarEvent({
                title: info.event.title,
                start: info.event.start?.toISOString() || '',
                end: info.event.end?.toISOString() || '',
                source: info.event.extendedProps?.source,
                notes:
                  info.event.extendedProps?.source === 'free_slot'
                    ? formatFreeSlotLabel(info.event.extendedProps?.notes as string | undefined)
                    : info.event.title,
                link: eventLink,
                slotId:
                  typeof info.event.extendedProps?.slotId === 'number'
                    ? info.event.extendedProps.slotId
                    : undefined,
              });
            }}
          />
        </div>
      </div>

      {showFreeSlotsPage && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="min-h-full p-6 flex items-center justify-center">
            <div className="w-full max-w-3xl bg-white rounded-2xl border border-gray-100 shadow-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="live-calendar-section-title">
                  Your Free Slots ({candidateFreeSlots.length})
                </h3>
                <Button variant="outline" onClick={() => setShowFreeSlotsPage(false)}>
                  Close
                </Button>
              </div>

              {candidateFreeSlots.length === 0 ? (
                <p className="text-gray-500">No free slots added yet.</p>
              ) : (
                <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200">
                  <ul className="divide-y divide-gray-100">
                    {candidateFreeSlots.map((slot) => (
                      <li
                        key={slot.id}
                        className="px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <span className="block text-sm text-gray-700">
                            {new Date(slot.start_time).toLocaleDateString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          <span className="block text-sm font-medium text-gray-900">
                            {new Date(slot.start_time).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}{' '}
                            -{' '}
                            {new Date(slot.end_time).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditFreeSlot(slot)}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            disabled={freeSlotActionLoading === slot.id}
                            onClick={() => requestDeleteFreeSlot(slot)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedCalendarEvent && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-gray-100 shadow-2xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedCalendarEvent.title}
            </h3>
            <p className="text-sm text-gray-600 mb-1">
              {new Date(selectedCalendarEvent.start).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
            <p className="text-sm text-gray-600 mb-3">
              {new Date(selectedCalendarEvent.start).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              -{' '}
              {new Date(selectedCalendarEvent.end).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>

            {selectedCalendarEvent.notes ? (
              <p className="text-sm text-gray-700 mb-4">{selectedCalendarEvent.notes}</p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              {selectedCalendarEvent.source === 'free_slot' && selectedCalendarEvent.slotId ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const slot = savedCalendarEvents.find(
                        (entry) => entry.id === selectedCalendarEvent.slotId
                      );
                      if (slot) openEditFreeSlot(slot);
                    }}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    disabled={freeSlotActionLoading === selectedCalendarEvent.slotId}
                    onClick={() => {
                      const slot = savedCalendarEvents.find(
                        (entry) => entry.id === selectedCalendarEvent.slotId
                      );
                      if (slot) {
                        requestDeleteFreeSlot(slot);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </>
              ) : (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  disabled={!selectedCalendarEvent.link}
                  onClick={() => {
                    if (selectedCalendarEvent.link) {
                      window.open(selectedCalendarEvent.link, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  Join Meeting <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedCalendarEvent(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={pendingDeleteSlot != null}
        onOpenChange={(open) => {
          if (!open && freeSlotActionLoading == null) {
            setPendingDeleteSlot(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete free slot?</DialogTitle>
            <DialogDescription>
              {pendingDeleteSlot
                ? `Remove ${pendingDeleteSlot.label}? Recruiters will no longer be able to book this time.`
                : 'Remove this free slot? Recruiters will no longer be able to book this time.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              disabled={freeSlotActionLoading != null}
              onClick={() => setPendingDeleteSlot(null)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={freeSlotActionLoading != null}
              onClick={() => void confirmDeleteFreeSlot()}
            >
              {freeSlotActionLoading != null ? 'Deleting…' : 'Delete slot'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showManualModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 py-8">
            <LiveCalendarCompat
              candidateId={user?.id}
              editingSlot={editingFreeSlot}
              onClose={() => {
                setShowManualModal(false);
                setEditingFreeSlot(null);
              }}
              onSaved={async () => {
                refreshCalendarData();
                setEditingFreeSlot(null);
              }}
            />
          </div>
        </div>
      )}

      {showCongratsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 text-center">
            <h3 className="text-lg font-semibold mb-2 text-gray-900">🎉 Congratulations!</h3>
            <p className="text-gray-700 mb-4">
              Your Google Calendar has been successfully connected.
            </p>
            <button
              onClick={() => setShowCongratsModal(false)}
              className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
